
import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, AlertTriangle, Target, Zap, CheckCircle, TrendingUp, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const RansomwareAsAService = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Back Link */}
        <div className="container mx-auto px-4 md:px-6 pt-6">
          <Button variant="ghost" asChild className="text-[#8932c8] hover:text-[#7a2cb3] hover:bg-[#8932c8]/5">
            <Link to="/blogs" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Blog
            </Link>
          </Button>
        </div>

        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm font-medium bg-[#8932c8]/10 text-[#8932c8] px-3 py-1 rounded-full">
                  Ransomware Defense
                </span>
                <span className="text-sm text-gray-500">May 25, 2023 • 12 min read</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                The Rise of Ransomware-as-a-Service: How to Stay Ahead with AI-Driven XDR
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Explore the alarming growth of Ransomware-as-a-Service (RaaS) operations and discover how AI-powered XDR platforms provide the advanced defense capabilities needed to detect, prevent, and respond to these sophisticated threats.
              </p>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>By David Chang</span>
                <span>•</span>
                <span>Threat Intelligence Director</span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto prose prose-lg">
              
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-lg leading-relaxed">
                  The cybercriminal landscape has undergone a dramatic transformation in recent years. What was once the domain of highly skilled individual hackers has evolved into a sophisticated ecosystem of criminal enterprises offering "Ransomware-as-a-Service" (RaaS). This business model has democratized ransomware attacks, making them accessible to less technically skilled criminals while dramatically increasing the volume and sophistication of threats facing organizations worldwide.
                </p>
                <p className="text-lg leading-relaxed">
                  In 2023 alone, ransomware attacks increased by 41%, with RaaS operations accounting for over 60% of all incidents. Traditional security approaches are proving inadequate against these evolving threats, creating an urgent need for advanced AI-powered defense mechanisms that can detect and respond to ransomware attacks before they cause devastating damage.
                </p>
              </div>

              {/* Understanding RaaS */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Understanding Ransomware-as-a-Service (RaaS)</h2>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8">
                  <div className="flex items-start">
                    <AlertTriangle className="h-6 w-6 text-red-500 mr-3 mt-1" />
                    <div>
                      <p className="font-semibold text-red-800">Critical Threat Evolution</p>
                      <p className="text-red-700">RaaS has lowered the barrier to entry for ransomware attacks, resulting in a 300% increase in the number of active ransomware groups since 2020.</p>
                    </div>
                  </div>
                </div>

                <h3 className="text-2xl font-semibold mb-4 text-gray-900">How RaaS Operations Work</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h4 className="font-semibold text-blue-800 mb-3">The Operators (Developers)</h4>
                    <ul className="text-blue-700 space-y-2 text-sm">
                      <li>• Develop ransomware tools and infrastructure</li>
                      <li>• Maintain command and control servers</li>
                      <li>• Provide technical support to affiliates</li>
                      <li>• Handle payment processing and negotiations</li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                    <h4 className="font-semibold text-purple-800 mb-3">The Affiliates (Attackers)</h4>
                    <ul className="text-purple-700 space-y-2 text-sm">
                      <li>• Purchase or lease ransomware tools</li>
                      <li>• Conduct initial access and reconnaissance</li>
                      <li>• Deploy ransomware within target networks</li>
                      <li>• Share profits with operators (20-40%)</li>
                    </ul>
                  </div>
                </div>

                <h3 className="text-2xl font-semibold mb-4 text-gray-900">Major RaaS Groups and Their Tactics</h3>

                <div className="space-y-4 mb-8">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-2">LockBit</h4>
                    <p className="text-gray-700 text-sm">Known for fast encryption speeds and extensive affiliate network. Uses double extortion tactics and targets critical infrastructure.</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-2">BlackCat (ALPHV)</h4>
                    <p className="text-gray-700 text-sm">Cross-platform ransomware written in Rust. Employs sophisticated evasion techniques and aggressive extortion methods.</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-2">Cl0p</h4>
                    <p className="text-gray-700 text-sm">Focuses on supply chain attacks through third-party vulnerabilities. Notable for MOVEit and other file transfer exploits.</p>
                  </div>
                </div>
              </section>

              {/* RaaS Attack Lifecycle */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">The RaaS Attack Lifecycle: From Initial Access to Extortion</h2>

                <div className="space-y-6">
                  <div className="border-l-4 border-red-500 pl-6">
                    <h3 className="text-xl font-semibold mb-2 text-red-800">Phase 1: Initial Access (Days 1-3)</h3>
                    <p className="text-gray-700 mb-3">
                      Attackers gain entry through various vectors including phishing emails, remote desktop vulnerabilities, supply chain compromises, or purchased access from initial access brokers.
                    </p>
                    <div className="bg-red-50 p-3 rounded text-sm">
                      <strong>Common Techniques:</strong> Credential stuffing, exploit kits, social engineering, zero-day exploits, insider threats
                    </div>
                  </div>

                  <div className="border-l-4 border-orange-500 pl-6">
                    <h3 className="text-xl font-semibold mb-2 text-orange-800">Phase 2: Reconnaissance and Lateral Movement (Days 4-14)</h3>
                    <p className="text-gray-700 mb-3">
                      Attackers explore the network, map critical systems, identify valuable data, and establish persistence across multiple systems to maximize impact.
                    </p>
                    <div className="bg-orange-50 p-3 rounded text-sm">
                      <strong>AI Detection Opportunity:</strong> Unusual network scanning, privilege escalation attempts, abnormal file access patterns
                    </div>
                  </div>

                  <div className="border-l-4 border-yellow-500 pl-6">
                    <h3 className="text-xl font-semibold mb-2 text-yellow-800">Phase 3: Data Exfiltration (Days 10-20)</h3>
                    <p className="text-gray-700 mb-3">
                      Before encryption, attackers steal sensitive data to enable double extortion tactics, threatening to publish the information if ransom demands aren't met.
                    </p>
                    <div className="bg-yellow-50 p-3 rounded text-sm">
                      <strong>AI Detection Opportunity:</strong> Large data transfers, compression activities, unusual cloud uploads, data staging behaviors
                    </div>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-6">
                    <h3 className="text-xl font-semibold mb-2 text-purple-800">Phase 4: Ransomware Deployment (Day 15-30)</h3>
                    <p className="text-gray-700 mb-3">
                      Attackers deploy ransomware across the network, encrypt critical systems, delete backups, and present ransom demands with threats of data publication.
                    </p>
                    <div className="bg-purple-50 p-3 rounded text-sm">
                      <strong>AI Detection Opportunity:</strong> Mass file encryption, backup deletion, suspicious process execution, network communication anomalies
                    </div>
                  </div>
                </div>
              </section>

              {/* How AI XDR Detects RaaS */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">How AI-Powered XDR Defeats RaaS Operations</h2>

                <div className="space-y-8">
                  <div className="border-l-4 border-[#8932c8] pl-6">
                    <h3 className="text-2xl font-semibold mb-3 text-gray-900">1. Early-Stage Detection Through Behavioral Analytics</h3>
                    <p className="text-gray-700 mb-4">
                      AI algorithms establish behavioral baselines for users, devices, and applications, enabling detection of subtle anomalies that indicate the early stages of a ransomware attack before encryption begins.
                    </p>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-2">Key Behavioral Indicators</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-700">
                        <div>• Unusual login patterns</div>
                        <div>• Abnormal file access volumes</div>
                        <div>• Suspicious network reconnaissance</div>
                        <div>• Privilege escalation attempts</div>
                        <div>• Lateral movement patterns</div>
                        <div>• Backup system access anomalies</div>
                      </div>
                    </div>
                  </div>

                  <div className="border-l-4 border-[#8932c8] pl-6">
                    <h3 className="text-2xl font-semibold mb-3 text-gray-900">2. Advanced Threat Hunting with Machine Learning</h3>
                    <p className="text-gray-700 mb-4">
                      Machine learning models continuously analyze network traffic, file system changes, and process execution patterns to identify indicators of compromise (IOCs) associated with known RaaS operations.
                    </p>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-2">ML Detection Capabilities</h4>
                      <ul className="text-green-700 space-y-1 text-sm">
                        <li>• Encryption behavior pattern recognition</li>
                        <li>• Command and control communication detection</li>
                        <li>• File entropy analysis for encryption detection</li>
                        <li>• Process injection and hollowing identification</li>
                      </ul>
                    </div>
                  </div>

                  <div className="border-l-4 border-[#8932c8] pl-6">
                    <h3 className="text-2xl font-semibold mb-3 text-gray-900">3. Predictive Analytics for Threat Forecasting</h3>
                    <p className="text-gray-700 mb-4">
                      AI-powered predictive models analyze global threat intelligence, vulnerability data, and attack trends to forecast potential ransomware campaigns targeting specific industries or technologies.
                    </p>
                  </div>

                  <div className="border-l-4 border-[#8932c8] pl-6">
                    <h3 className="text-2xl font-semibold mb-3 text-gray-900">4. Automated Incident Response and Containment</h3>
                    <p className="text-gray-700 mb-4">
                      When ransomware indicators are detected, AI-driven response automation immediately executes containment actions such as network isolation, process termination, and backup protection to minimize damage.
                    </p>
                  </div>
                </div>
              </section>

              {/* Defense Strategies */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Comprehensive RaaS Defense Strategy with AI XDR</h2>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <Shield className="h-6 w-6 text-blue-500 mr-3 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Proactive Threat Detection</h3>
                        <p className="text-gray-700 text-sm">Continuous monitoring for ransomware precursors, including reconnaissance activities, privilege escalation attempts, and unusual data access patterns.</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <Target className="h-6 w-6 text-red-500 mr-3 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Endpoint Protection Integration</h3>
                        <p className="text-gray-700 text-sm">Deep integration with endpoint detection and response tools to provide comprehensive visibility into process execution and file system changes.</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <Zap className="h-6 w-6 text-yellow-500 mr-3 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Real-Time Response Automation</h3>
                        <p className="text-gray-700 text-sm">Immediate automated response to ransomware indicators, including network segmentation, process termination, and backup isolation.</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-start">
                      <Lock className="h-6 w-6 text-purple-500 mr-3 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Backup Protection</h3>
                        <p className="text-gray-700 text-sm">AI-powered monitoring of backup systems and processes to detect and prevent ransomware attacks on critical recovery infrastructure.</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <TrendingUp className="h-6 w-6 text-green-500 mr-3 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Threat Intelligence Integration</h3>
                        <p className="text-gray-700 text-sm">Real-time integration with global threat intelligence feeds to identify emerging RaaS campaigns and tactics.</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <CheckCircle className="h-6 w-6 text-blue-500 mr-3 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Recovery Orchestration</h3>
                        <p className="text-gray-700 text-sm">Automated recovery procedures that prioritize critical systems and ensure clean restoration from verified backup sources.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Case Studies */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Real-World Success Stories: AI XDR vs. RaaS</h2>

                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-3 text-green-800">Healthcare System Prevents LockBit Attack</h3>
                    <p className="text-green-700 mb-3">
                      A major healthcare system's AI XDR platform detected unusual credential usage and lateral movement patterns 5 days before planned ransomware deployment, enabling complete threat elimination with zero business impact.
                    </p>
                    <div className="bg-green-100 p-3 rounded text-sm text-green-600">
                      <strong>Key Success Factors:</strong> Behavioral analytics, automated response, comprehensive visibility across medical devices and IT systems
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-3 text-blue-800">Manufacturing Company Stops BlackCat Affiliate</h3>
                    <p className="text-blue-700 mb-3">
                      AI-powered threat hunting identified a BlackCat affiliate's reconnaissance activities within 48 hours of initial access, preventing a planned attack on critical production systems.
                    </p>
                    <div className="bg-blue-100 p-3 rounded text-sm text-blue-600">
                      <strong>Key Success Factors:</strong> OT/IT network monitoring, machine learning correlation, rapid incident response
                    </div>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-3 text-purple-800">Financial Institution Defeats Cl0p Operation</h3>
                    <p className="text-blue-700 mb-3">
                      Predictive analytics identified indicators of a planned Cl0p attack based on reconnaissance patterns, enabling proactive hardening and complete attack prevention.
                    </p>
                    <div className="bg-purple-100 p-3 rounded text-sm text-purple-600">
                      <strong>Key Success Factors:</strong> Predictive modeling, threat intelligence integration, proactive defense measures
                    </div>
                  </div>
                </div>
              </section>

              {/* Implementation Roadmap */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">RaaS Defense Implementation Roadmap</h2>

                <div className="space-y-6">
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-6">
                    <h3 className="text-xl font-semibold mb-3 text-blue-800">Phase 1: Foundation (Weeks 1-4)</h3>
                    <ul className="space-y-2 text-blue-700">
                      <li>• Deploy AI XDR platform across critical systems</li>
                      <li>• Establish baseline behavioral analytics</li>
                      <li>• Integrate with existing security tools</li>
                      <li>• Configure initial detection rules for known RaaS TTPs</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 border-l-4 border-green-500 p-6">
                    <h3 className="text-xl font-semibold mb-3 text-green-800">Phase 2: Enhancement (Weeks 5-8)</h3>
                    <ul className="space-y-2 text-green-700">
                      <li>• Enable advanced threat hunting capabilities</li>
                      <li>• Implement automated response playbooks</li>
                      <li>• Train security team on RaaS-specific scenarios</li>
                      <li>• Conduct tabletop exercises for ransomware incidents</li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 border-l-4 border-purple-500 p-6">
                    <h3 className="text-xl font-semibold mb-3 text-purple-800">Phase 3: Optimization (Weeks 9-12)</h3>
                    <ul className="space-y-2 text-purple-700">
                      <li>• Fine-tune AI models based on environment specifics</li>
                      <li>• Implement predictive analytics capabilities</li>
                      <li>• Expand coverage to cloud and remote environments</li>
                      <li>• Establish continuous improvement processes</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Future Threats */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Emerging RaaS Trends and Future Threats</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <h3 className="font-semibold text-red-800 mb-3">AI-Powered Ransomware</h3>
                    <p className="text-red-700 text-sm mb-2">Ransomware operations incorporating AI for target selection, evasion techniques, and dynamic encryption methods.</p>
                    <p className="text-red-600 text-xs"><strong>Defense:</strong> Counter-AI techniques and advanced behavioral analytics</p>
                  </div>

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                    <h3 className="font-semibold text-orange-800 mb-3">Cloud-Native RaaS</h3>
                    <p className="text-orange-700 text-sm mb-2">Ransomware specifically designed for cloud environments, leveraging cloud APIs and services for deployment and persistence.</p>
                    <p className="text-orange-600 text-xs"><strong>Defense:</strong> Cloud-native AI XDR with comprehensive API monitoring</p>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                    <h3 className="font-semibold text-purple-800 mb-3">Supply Chain RaaS</h3>
                    <p className="text-purple-700 text-sm mb-2">Targeted attacks on software supply chains to distribute ransomware through legitimate update mechanisms.</p>
                    <p className="text-purple-600 text-xs"><strong>Defense:</strong> Supply chain monitoring and code integrity verification</p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="font-semibold text-blue-800 mb-3">IoT and OT Ransomware</h3>
                    <p className="text-blue-700 text-sm mb-2">Expansion into Internet of Things and operational technology environments with potential for physical world impact.</p>
                    <p className="text-blue-600 text-xs"><strong>Defense:</strong> Specialized IoT/OT monitoring and segmentation strategies</p>
                  </div>
                </div>
              </section>

              {/* Conclusion */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Conclusion: Staying Ahead of the RaaS Evolution</h2>
                <p className="text-lg mb-6">
                  The Ransomware-as-a-Service model has fundamentally changed the threat landscape, making sophisticated ransomware attacks accessible to a broader range of criminals. Traditional security approaches that rely on signatures and known indicators are insufficient against these rapidly evolving threats.
                </p>
                <p className="text-lg mb-8">
                  Organizations must adopt AI-powered XDR platforms that can detect ransomware attacks in their early stages, before encryption begins. The ability to identify subtle behavioral anomalies, correlate disparate events, and automatically respond to threats is no longer a luxury – it's a necessity for survival in today's threat landscape.
                </p>

                <div className="bg-[#8932c8] text-white p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-3">Protect Your Organization from RaaS Threats</h3>
                  <p className="mb-4">Discover how Spharaka's AgentK9 platform provides comprehensive protection against ransomware-as-a-service operations.</p>
                  <Button asChild className="bg-white text-[#8932c8] hover:bg-gray-100">
                    <Link to="/agent-k9-technology">Explore Advanced Ransomware Protection</Link>
                  </Button>
                </div>
              </section>

              {/* FAQ Section */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Frequently Asked Questions</h2>
                
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger>How does AI XDR detect ransomware before encryption begins?</AccordionTrigger>
                    <AccordionContent>
                      AI XDR uses behavioral analytics to identify the precursor activities that occur before ransomware deployment, such as reconnaissance, lateral movement, privilege escalation, and data staging. Machine learning algorithms establish baselines for normal behavior and detect deviations that indicate ransomware preparation activities, often days or weeks before encryption begins.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-2">
                    <AccordionTrigger>What makes RaaS operations more dangerous than traditional ransomware?</AccordionTrigger>
                    <AccordionContent>
                      RaaS operations are more dangerous because they combine the technical expertise of skilled developers with a broad network of affiliates, dramatically increasing the volume and sophistication of attacks. They also employ professional business practices, customer support, and continuous improvement, making them more effective and persistent than individual ransomware campaigns.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-3">
                    <AccordionTrigger>How can organizations protect their backups from ransomware attacks?</AccordionTrigger>
                    <AccordionContent>
                      Modern ransomware groups specifically target backup systems. Protection strategies include implementing immutable backups, maintaining air-gapped copies, using the 3-2-1 backup rule, monitoring backup system access with AI analytics, and ensuring backup restoration procedures are tested and secured. AI XDR can monitor backup environments for suspicious activities and automatically protect backup systems when ransomware is detected.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-4">
                    <AccordionTrigger>What should organizations do if they discover an active ransomware attack?</AccordionTrigger>
                    <AccordionContent>
                      Immediate steps include isolating affected systems, activating incident response procedures, preserving evidence, assessing the scope of compromise, and beginning recovery from clean backups. AI XDR platforms can automate many of these initial response actions, buying valuable time for the incident response team to coordinate a comprehensive response strategy.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-5">
                    <AccordionTrigger>How does threat intelligence help in defending against RaaS operations?</AccordionTrigger>
                    <AccordionContent>
                      Threat intelligence provides crucial insights into RaaS group tactics, techniques, and procedures (TTPs), enabling proactive defense measures. AI XDR platforms integrate threat intelligence feeds to automatically update detection rules, identify emerging threats, and correlate local activities with global attack campaigns. This intelligence-driven approach enables organizations to defend against known RaaS operations and predict potential attack vectors.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </section>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default RansomwareAsAService;
